import { useEffect, useMemo } from "react";
import { useLocale } from "next-intl";
import type { ShippingPrice } from "@components/commerce/order/types";
import { activeSiteConfig } from "@config/site/site-config";
import { findHomeDelivery } from "@lib/utils/findDeliveryMethods";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";
import { useEstimatedDeliveryRange } from "@hooks/useEstimatedDeliveryDate";
import { isRemotePortugalIslandPostalCode } from "../utils/isRemotePortugalIsland";
import { shouldHideExpressDelivery } from "../utils/shouldHideExpressDelivery";

export const EXPRESS_FULFILLMENT_OPTION = "HD_EXPR";

const isExpressMethod = (method: ShippingPrice) =>
    method.fulfillmentOption === EXPRESS_FULFILLMENT_OPTION;

interface UseDisplayedShippingMethodsParams {
    shippingMethods: ShippingPrice[] | undefined;
    postalCode: string | undefined;
    deliveryMethodId: string;
    onAutoSelect: (method: ShippingPrice) => void;
}

interface UseDisplayedShippingMethodsResult {
    displayedMethods: ShippingPrice[] | undefined;
    isPtIsland: boolean;
    // Exposed so callers can render Express's own delivery-date estimate without re-deriving
    // which method is Express.
    expressMethod: ShippingPrice | undefined;
}

/**
 * Applies the two business rules that override which shipping methods are selectable, and
 * keeps the selected method in sync whenever a rule flips the visible list out from under it:
 *
 * 1. Remote Portugal islands: only Express can fulfil these postal codes, so it becomes the
 *    sole option shown and is auto-selected.
 * 2. Express with no real advantage: in markets where the "hasEstimatedDeliveryDate" flag is
 *    on, Express is dropped once its fastest promised day is no sooner than the regular
 *    estimated delivery date - at that point it's a slower-looking duplicate of Home Delivery,
 *    not a real upgrade. If Express was selected when this kicks in, selection falls back to
 *    Home Delivery so the form is never left pointing at a method that's no longer on screen.
 *    Markets without that flag never run this comparison, so Express is unaffected there.
 *
 * Rule 1 takes precedence over rule 2: on a PT island, Express is shown (and required)
 * regardless of what the estimate comparison says.
 */
export const useDisplayedShippingMethods = ({
    shippingMethods,
    postalCode,
    deliveryMethodId,
    onAutoSelect,
}: UseDisplayedShippingMethodsParams): UseDisplayedShippingMethodsResult => {
    const locale = useLocale();
    const hasEstimatedDeliveryDate = useFeatureFlag("hasEstimatedDeliveryDate");
    const estimatedDeliveryRange = useEstimatedDeliveryRange();

    const isPtIsland = isRemotePortugalIslandPostalCode(postalCode, activeSiteConfig.country);

    const expressMethod = useMemo(() => shippingMethods?.find(isExpressMethod), [shippingMethods]);

    // Markets that don't support estimated delivery dates have nothing to compare Express
    // against, so it's always worth showing there. Elsewhere, no estimate resolved yet (still
    // loading, or unavailable) also defaults to showing Express until there's a real comparison.
    const isExpressWorthShowing =
        !hasEstimatedDeliveryDate ||
        !shouldHideExpressDelivery(
            expressMethod?.deliveryTime?.[locale],
            estimatedDeliveryRange?.earliest
        );

    const displayedMethods = useMemo(() => {
        if (isPtIsland) return shippingMethods?.filter(isExpressMethod);
        if (isExpressWorthShowing) return shippingMethods;
        return shippingMethods?.filter((method) => !isExpressMethod(method));
    }, [shippingMethods, isPtIsland, isExpressWorthShowing]);

    useEffect(() => {
        if (!isPtIsland || !expressMethod || expressMethod.key === deliveryMethodId) return;
        onAutoSelect(expressMethod);
    }, [isPtIsland, expressMethod, deliveryMethodId]);

    useEffect(() => {
        if (isPtIsland || isExpressWorthShowing || deliveryMethodId !== expressMethod?.key) return;
        // findHomeDelivery only matches orderType "HD", which isn't guaranteed to exist (e.g. a
        // market offering only Express + Parcel Shop). Fall back to whatever's first in the
        // still-visible list so the form is never left pointing at the now-hidden Express method.
        const fallbackMethod = findHomeDelivery(shippingMethods) ?? displayedMethods?.[0];
        if (fallbackMethod) onAutoSelect(fallbackMethod);
    }, [
        isPtIsland,
        isExpressWorthShowing,
        deliveryMethodId,
        expressMethod,
        shippingMethods,
        displayedMethods,
    ]);

    return { displayedMethods, isPtIsland, expressMethod };
};
